import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminOrdersApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";

const STATUS_TONE = {
  NEW: "bg-blue-100 text-blue-800",
  UNDER_REVIEW: "bg-blue-100 text-blue-800",
  VERIFIED: "bg-emerald-100 text-emerald-800",
  PAYMENT_CONFIRMED: "bg-emerald-100 text-emerald-800",
  READY_FOR_DELIVERY: "bg-indigo-100 text-indigo-800",
  OUT_FOR_DELIVERY: "bg-purple-100 text-purple-800",
  DELIVERED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  CANCELLED: "bg-slate-100 text-slate-800",
};

export default function AdminOrders() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 25 };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      setData(await adminOrdersApi.list(params));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [page, statusFilter]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-orders-root">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Orders</div>
            <div className="text-lg font-semibold">Order operations</div>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline"><Link to="/admin">&larr; Dashboard</Link></Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Filter</CardTitle></CardHeader>
          <CardContent className="flex gap-3 flex-wrap items-center">
            <form onSubmit={onSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by order #, name, email, phone"
                data-testid="admin-orders-search"
              />
              <Button type="submit" variant="secondary">Search</Button>
            </form>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-56" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="NEW">New (pending review)</SelectItem>
                <SelectItem value="VERIFIED">Verified</SelectItem>
                <SelectItem value="READY_FOR_DELIVERY">Ready for delivery</SelectItem>
                <SelectItem value="OUT_FOR_DELIVERY">Out for delivery</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground ml-auto">{data.total} orders</div>
          </CardContent>
        </Card>

        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="p-3 text-left">Order #</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Emirate</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Placed</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id} className="border-t" data-testid={`admin-order-row-${o.order_number}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td>
                  <td className="p-3">
                    <div>{o.customer?.name}</div>
                    <div className="text-xs text-muted-foreground">{o.customer?.phone}</div>
                  </td>
                  <td className="p-3 text-xs">{o.delivery_address?.emirate}</td>
                  <td className="p-3 text-right font-medium">
                    {formatMoney({ amount_minor: o.totals?.grand_total_minor || 0, currency: o.totals?.currency || "AED" })}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-1 rounded text-[11px] font-medium ${STATUS_TONE[o.status] || "bg-slate-100"}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td className="p-3 text-right">
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/admin/orders/${o.id}`} data-testid={`admin-order-open-${o.order_number}`}>Open</Link>
                    </Button>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && !busy && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data.total > 25 && (
          <div className="flex justify-between items-center">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-orders-prev">Previous</Button>
            <div className="text-sm text-muted-foreground">Page {page}</div>
            <Button variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)} data-testid="admin-orders-next">Next</Button>
          </div>
        )}
      </main>
    </div>
  );
}
